import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { OrderStateServiceV2 } from '../../order-state.service';
import { OrderFlowType } from '../../../../../../models/order/order.model';
import { WirelessProductPlusResponseService } from '../../order-api-response/wireless-pd-plus-response.service';
import { LineConfigType, WirelessCartCustomizationType, WirelessCartData, WirelessCartLineData, WirelessCartSelectedCustomization } from '../../../../../../models/order/workflow/wireless/wireless-cart.model';
import { CartCharges, CartItem, CartItemType, CartSectionType } from '../../../../../../models/order/workflow/order-cart.model';
import { WirelessFlowStateService } from '../wireless/wireless-flow-state.service';
import { WirelessCartRuleEngine } from '../../cart-rules/wireless-cart-rule-engine';
import { NoContractDeviceCartRule, InstallmentNextUpAnytimeCartRule, WirelessCartRuleContext, WirelessLineRequiredDownpaymentCartRule, WirelessCartRuleType, WirelessLineOptionalDownpaymentCartRule, OQResponseTodaysChargesCartRule, WirelessSelectedDeviceCartRule, WirelessSelectedPlanCartRule, WirelessSelectedInstallmentPlanCartRule, WirelessProtectionPlanCartRule, WirelessPromotionCartRule, WirelessSelectedDeviceDefaultChoicesCartRule, WirelessABPDiscountCartRule, WirelessExistingABPDiscountCartRule, WirelessDeliveryServiceOptionCartRule } from '../../cart-rules/wireless-cart-rules';
import { WirelessLineConfigurationStateService } from '../wireless/wireless-line-configuration.state.service';
import { OrderWirelessCustomizationsStateService } from '../order-customization/order-wireless-customization.state.service';
import { OrderQualificationResponseService } from '../../order-api-response/order-oq-response.service';
import { WirelessAdditionalOffersStateService } from '../wireless/wireless-additional-offers.state.service';
import { WirelessProductResponseService } from '../../order-api-response/wireless-pd-response.service';
import { OrderCustomizationsStateService } from '../order-customization/order-customization.state.service';
import { OrderPaymentResponseService } from '../../order-api-response/order-payment.service';
import { WirelessSetupStateService } from '../wireless/wireless-setup.state.service';
import { isLineNewOrTouched } from '../../../../../../util/wireless-line-config-util';


@Injectable({ providedIn: 'root' })
export class WirelessCartStateService {
    private orderStateService = inject(OrderStateServiceV2);
    private wirelessPDPlusResponseService = inject(WirelessProductPlusResponseService);
    private wirelessProductResponseService = inject(WirelessProductResponseService);
    private orderWirelessCustomizationsStateService = inject(OrderWirelessCustomizationsStateService);
    private wirelessFlowStateService = inject(WirelessFlowStateService);
    private wirelessLineConfigStateService = inject(WirelessLineConfigurationStateService);
    private orderCustomizations = this.orderWirelessCustomizationsStateService.selectedCustomizations;
    private _cart = signal<WirelessCartData | null>(null);
    private ruleEngine = new WirelessCartRuleEngine();
    private orderQualificationResponseService = inject(OrderQualificationResponseService);
    private wirelessAdditionalOffersStateService = inject(WirelessAdditionalOffersStateService);
    private orderCustomizationsStateService = inject(OrderCustomizationsStateService);
    private paymentService = inject(OrderPaymentResponseService);
    private wirelessSetupStateService = inject(WirelessSetupStateService);


    selectedCustomizations: WirelessCartSelectedCustomization[] = [];
    cart = this._cart.asReadonly();

    constructor() {
        this.initializeRules();
        effect(() => {
            const orderWirelessCustomizations = this.orderCustomizations();
            const order = this.orderStateService.order();
            const orderCustomizations = this.orderCustomizationsStateService.selectedCustomizations();
            const wirelessPdResponse = this.wirelessPDPlusResponseService.productResponse();
            this._cart.set(null);
            this.selectedCustomizations = [];
            if (orderWirelessCustomizations || wirelessPdResponse || orderCustomizations || order) {
                this.selectedCustomizations = [];
                this.setupWirelessDefaultCustomization();
                this.setupSelectedCustomizations();
                this.setupCartFromOrder();
            }
        });
    }

    private initializeRules() {
        this.ruleEngine.registerRule(new WirelessSelectedDeviceCartRule());
        this.ruleEngine.registerRule(new WirelessSelectedPlanCartRule());
        this.ruleEngine.registerRule(new WirelessSelectedInstallmentPlanCartRule());
        this.ruleEngine.registerRule(new NoContractDeviceCartRule());
        this.ruleEngine.registerRule(new InstallmentNextUpAnytimeCartRule());
        this.ruleEngine.registerRule(new WirelessLineOptionalDownpaymentCartRule());
        this.ruleEngine.registerRule(new WirelessLineRequiredDownpaymentCartRule());
        this.ruleEngine.registerRule(new OQResponseTodaysChargesCartRule());
        this.ruleEngine.registerRule(new WirelessProtectionPlanCartRule());
        this.ruleEngine.registerRule(new WirelessPromotionCartRule());
        this.ruleEngine.registerRule(new WirelessSelectedDeviceDefaultChoicesCartRule());
        this.ruleEngine.registerRule(new WirelessABPDiscountCartRule());
        this.ruleEngine.registerRule(new WirelessExistingABPDiscountCartRule());
        this.ruleEngine.registerRule(new WirelessDeliveryServiceOptionCartRule());

    }

    buildCartItems(selectedCustomizations: WirelessCartSelectedCustomization[]): CartItem[] {
        const activeLineOffer = this.wirelessFlowStateService.state()?.latestPdPlusCallHappenedOnLine;
        const contexts: WirelessCartRuleContext[] = selectedCustomizations.filter(x => x.type !== WirelessCartCustomizationType.OrderQualification).map(custom => ({
            selectedCustomizations: custom,
            allSelectedCustomizations: selectedCustomizations,
            cartItems: [],
            lineNumber: custom.lineNumber,
            lineData: this.wirelessLineConfigStateService.getLineConfiguration(custom.lineNumber || 0),
            wirelessPDPlusData: this.wirelessPDPlusResponseService.productResponse(),
            activeLineNumber: activeLineOffer || null,
            oqData: this.orderQualificationResponseService.orderOQResponse(),
            viPaymentData: this.paymentService.viPaymentProductResponse(),
            cardType: this.paymentService.wirelessPaymentCardType(),
            wirelessStateData: this.wirelessFlowStateService.state()
        } as WirelessCartRuleContext));

        const cartItems = this.ruleEngine.processAllCustomizations(contexts);

        const oqCustomization = selectedCustomizations.find(x => x.type === WirelessCartCustomizationType.OrderQualification);
        if (oqCustomization) {
            const oqContext: WirelessCartRuleContext = {
                selectedCustomizations: oqCustomization,
                allSelectedCustomizations: selectedCustomizations,
                cartItems: [],
                wirelessPDPlusData: this.wirelessPDPlusResponseService.productResponse(),
                oqData: this.orderQualificationResponseService.orderOQResponse(),
                wirelessStateData: this.wirelessFlowStateService.state()
            };
            const oqCartItems = this.ruleEngine.processAllCustomizations([oqContext], WirelessCartRuleType.OrderQualification);
            cartItems.push(...oqCartItems);
        }



        return cartItems;
    }

    private setupCartFromOrder() {

        const cartItems = this.buildCartItems(this.selectedCustomizations);
        const cartData: WirelessCartData = this.setupWirelessMonthlyCharges(cartItems);
        if (cartData) {
            cartData.firstBillCharges = this.setupFirstBillCharges(cartItems);
            cartData.todaysCharges = this.setupTodaysCharges(cartItems);
            cartData.savingAndPromotionCharges = this.setupSavingAndPromotionCharges(cartItems);
            this._cart.set(cartData);
        }


    }

    private setupWirelessDefaultCustomization() {
        const defaultLineOffers = this.wirelessPDPlusResponseService.findCartSelectedCustomization();
        //This will give customization came back with the selected device
        for (const line of defaultLineOffers) {
            for (const offer of line.offers) {
                const customization = this.wirelessPDPlusResponseService.findCustomizationByCode(line.lineNumber, offer.customizationCode);
                this.selectedCustomizations.push({ Customization: customization, Choice: offer.choice, lineNumber: line.lineNumber, type: WirelessCartCustomizationType.Line, lineType: LineConfigType.Offers });
            }
        }

        const findChoiceAndAddCustomization = (lineNumber: number, choiceCode: string, configType: LineConfigType) => {
            const choice = this.wirelessPDPlusResponseService.findChoiceByCode(lineNumber, choiceCode);
            if (choice) {
                this.selectedCustomizations.push({ Customization: choice.customization, Choice: choice.choice, lineNumber: lineNumber, type: WirelessCartCustomizationType.Line, lineType: configType });
            }
        };

        const wirelessLineConfigs = this.orderStateService.order()?.wirelessFlow?.lineConfigurations;
        if (wirelessLineConfigs) {
            Object.keys(wirelessLineConfigs).forEach(lineKey => {
                const lineConfig = wirelessLineConfigs[lineKey];
                const lineNumber = lineConfig?.device?.lineNumber;
                if (!lineNumber) return;
                // Skip existing lines the user is not changing. Only new lines and in-progress
                // upgrades belong in the cart
                if (!isLineNewOrTouched(lineConfig)) return;
                //Device Info
                if (lineConfig.device) {
                    switch (lineConfig.device.deviceType) {
                        case 'NEW':
                        case 'ONHAND':
                            {
                                const deviceChoiceCode = lineConfig.device?.selectedDevice?.ChoiceCode;
                                if (deviceChoiceCode) {
                                    findChoiceAndAddCustomization(lineNumber, deviceChoiceCode, LineConfigType.Device);
                                }
                                break;
                            }
                        case 'BYOD':
                            {
                                findChoiceAndAddCustomization(lineNumber, lineConfig.device.deviceType, LineConfigType.Device);
                                break;
                            }
                    }
                }

                const wirelessPlanChoiceCode = lineConfig.wirelessPlan?.planSelected;
                if (wirelessPlanChoiceCode) {
                    findChoiceAndAddCustomization(lineNumber, wirelessPlanChoiceCode, LineConfigType.Plan);
                }

                const installmentPlanChoiceCode = lineConfig.installmentPlan?.planSelected;
                if (installmentPlanChoiceCode) {
                    //AT&T Installment Plan with Next Up Anytime                    
                    findChoiceAndAddCustomization(lineNumber, installmentPlanChoiceCode, LineConfigType.Installment);
                }

                const protectionPlanChoiceCode = lineConfig.protectionPlan?.planSelected;
                if (protectionPlanChoiceCode) {
                    findChoiceAndAddCustomization(lineNumber, protectionPlanChoiceCode, LineConfigType.Protection);
                }

                if (lineConfig.promotions?.length) {
                    for (const promo of lineConfig.promotions) {

                        const promoChoiceCode = promo?.promotionCode?.length > 0 ? promo.promotionCode : (
                            promo?.customizationCode === 'WirelessTradeInPromotion' ? promo?.customizationCode : promo?.acceptChoiceCode);
                        if (promoChoiceCode && promo.isAccepted) {
                            findChoiceAndAddCustomization(lineNumber, promoChoiceCode, LineConfigType.Promotion);
                        }
                    }
                }

                //we will add default ABP promo if not already added
                if (this.wirelessAdditionalOffersStateService.hasABPOffer()) {
                    const abpPromo = this.wirelessAdditionalOffersStateService.getABPOffer();
                    const choice = this.wirelessProductResponseService.findChoiceByCode(abpPromo.choiceCode);
                    if (choice) {
                        choice.choice.CartVisible = 'true';
                        this.selectedCustomizations.push({ Customization: choice.customization, Choice: choice.choice, lineNumber: lineNumber, type: WirelessCartCustomizationType.Line, lineType: LineConfigType.Promotion });
                    } else if (this.wirelessSetupStateService.isExistingCustomerFlow()) {
                        //In case of existing wireless customer flow, we need to add the ABP discount even if the user has not selected the offer
                        this.selectedCustomizations.push({ Customization: { CustomizationCode: 'WirelessABPExisting' }, Choice: {}, lineNumber: lineNumber, type: WirelessCartCustomizationType.Line, lineType: LineConfigType.Promotion });
                    }
                }
            });
        }

        //This customization is to show the OQ related charges in the cart
        if (this.orderQualificationResponseService.orderOQResponse()) {
            this.selectedCustomizations.push({ Customization: {}, Choice: {}, type: WirelessCartCustomizationType.OrderQualification });
        }
    }

    private setupSelectedCustomizations() {
        const WirelessSelectedCustomization = this.orderCustomizations()?.filter(x => x.type == OrderFlowType.Wireless)
        if (WirelessSelectedCustomization.length) {
            const selectedCustomizations: WirelessCartSelectedCustomization[] = [];
            for (const customization of WirelessSelectedCustomization) {
                selectedCustomizations.push({ Customization: customization.customization, Choice: customization.choice, type: WirelessCartCustomizationType.Global });
            }

            selectedCustomizations.sort((a, b) => (a.Choice?.CartSeq || 0) - (b.Choice?.CartSeq || 0));
            this.selectedCustomizations.push(...selectedCustomizations);
        }
    }

    private setupFirstBillCharges(cartItems: CartItem[]): CartCharges | undefined {
        const firstBillCharges: CartCharges = {
            totalAmount: 0,
            items: []
        };

        if (cartItems.length > 0) {
            for (const item of cartItems) {
                if (item.cartSectionType === CartSectionType.FirstBill) {
                    const cartItem: CartItem = item
                    firstBillCharges.items.push(cartItem);
                    firstBillCharges.items.sort((a, b) => (a.seq || 0) - (b.seq || 0));
                    firstBillCharges.totalAmount += +cartItem.amount;
                }
            }
        }
        return firstBillCharges;
    }

    private setupTodaysCharges(cartItems: CartItem[]): CartCharges | undefined {
        const todayCharges: CartCharges = {
            totalAmount: 0,
            items: []
        };


        if (cartItems.length > 0) {
            for (const item of cartItems) {
                if (item.cartSectionType === CartSectionType.TodaysCharges) {
                    const cartItem: CartItem = item;
                    todayCharges.items.push(cartItem);
                    // Sort the items based on seq
                    todayCharges.items.sort((a, b) => (a.seq || 0) - (b.seq || 0));
                    todayCharges.totalAmount += +cartItem.amount;
                }
            }
        }


        return todayCharges;
    }

    private setupSavingAndPromotionCharges(cartItems: CartItem[]): CartCharges | undefined {
        const savingAndPromotionCharges: CartCharges = {
            totalAmount: 0,
            items: []
        };

        if (cartItems.length > 0) {
            for (const item of cartItems) {
                if (item.cartSectionType === CartSectionType.SavingsAndPromotions) {
                    const cartItem: CartItem = item
                    savingAndPromotionCharges.items.push(cartItem);
                    // Sort the items based on seq
                    savingAndPromotionCharges.items.sort((a, b) => (a.seq || 0) - (b.seq || 0));
                    savingAndPromotionCharges.totalAmount += +cartItem.amount;
                }
            }
        }

        return savingAndPromotionCharges;
    }

    private setupWirelessMonthlyCharges(cartItems: CartItem[]): WirelessCartData | undefined {
        if (cartItems.length === 0) return undefined;
        const cartData: WirelessCartData = {
            monthly: [],
            totalMonthlyAmount: 0
        };
        const monthlyLineOffers = cartItems.filter(item => item.cartSectionType === CartSectionType.Monthly);
        for (const item of monthlyLineOffers) {
            const cartItem: CartItem = item;
            const offer = item.metadata as WirelessCartSelectedCustomization;

            const lineExists = cartData.monthly?.find(line => line.lineNumber === offer.lineNumber);
            if (!lineExists) {
                const newLine: WirelessCartLineData = { lineNumber: offer.lineNumber, charges: { totalAmount: 0, items: [] }, promotions: { totalAmount: 0, items: [] }, totalAmount: 0 }
                const deviceInfo = cartItems.find(item => item.cartSectionType === CartSectionType.Device && (item.metadata as WirelessCartSelectedCustomization)?.lineNumber === offer.lineNumber);
                if (deviceInfo) {
                    newLine.device = deviceInfo
                }

                const lineConfig = this.wirelessLineConfigStateService.getLineConfiguration(offer.lineNumber || 0);
                const tradeInPromo = (lineConfig?.promotions ?? [])
                    .find(p => p.type === 'premiumTradeIn' && p.isAccepted && p.deviceDetails);
                const dd = tradeInPromo?.deviceDetails;
                if (dd) {
                    const tradeInValue = Number(dd.TradeInValue) || 0;
                    const promoDiscount = Number(dd.PromoDiscount) || 0;
                    const installment = this.selectedCustomizations.find(c => c.lineNumber === offer.lineNumber && c.lineType === LineConfigType.Installment);
                    const financingMonths = Number(installment?.Choice?.ContractTerm) || 0;
                    newLine.tradeIn = {
                        model: dd.TradeInModel,
                        tradeInValue,
                        promoDiscount,
                        financingMonths,
                        monthlyCredit: financingMonths > 0 ? Math.round((tradeInValue + promoDiscount) / financingMonths * 100) / 100 : 0,
                    };
                } else {
                    newLine.tradeIn = null;
                }
                cartData.monthly?.push(newLine);
            }

            const line = cartData.monthly?.find(line => line.lineNumber === offer.lineNumber);
            if (line) {
                if (cartItem.type === CartItemType.Fee) {
                    line.charges.items.push(cartItem);
                    line.charges.items.sort((a, b) => (a.seq || 0) - (b.seq || 0));
                    line.charges.totalAmount += +cartItem.amount;
                } else if (cartItem.type === CartItemType.Promotion) {
                    line.promotions.items.push(cartItem);
                    line.promotions.items.sort((a, b) => (a.seq || 0) - (b.seq || 0));
                    line.promotions.totalAmount += +cartItem.amount;
                }

                line.totalAmount = (line.charges.totalAmount || 0) + (line.promotions.totalAmount || 0) - (line.tradeIn?.monthlyCredit || 0);
            }
        }
        cartData.totalMonthlyAmount = cartData.monthly.filter(x => x.lineNumber > 0)?.reduce((acc, line) => acc + (line.totalAmount || 0), 0) || 0;
        return cartData;
    }

    isCartEmpty = computed(() => {
        return !this._cart() || this.selectedCustomizations.length === 0;
    })

    getTotalTodaysCharges(): number {
        const cart = this.cart();
        const requiredTodaysCharges = cart?.todaysCharges?.items?.filter(item => item.isRequired === true || item.isRequired === null || item.isRequired === undefined);
        return requiredTodaysCharges?.reduce((total, item) => total + +item.amount, 0) || 0;
    }
}
